import { useEffect, useState } from 'react';
import { useAuth } from '@/modules/auth/useAuth';
import { getSupabase } from '@/lib/supabase';
import { subscribeTable } from '@/lib/realtime';
import { derivePlanState, type Plan } from './plan';

export type { Plan } from './plan';
export { hasPremiumFeatures, hasVipFeatures, canBoost } from './plan';

export interface PlanState {
  plan: Plan;
  boostsRemaining: number;
  loading: boolean;
}

/**
 * One-shot read of `hottr_entitlements` for the current user, plus a
 * realtime subscription so a webhook-driven plan change reflects in the
 * UI without a refresh.
 */
export const usePlan = (): PlanState => {
  const { user } = useAuth();
  const [state, setState] = useState<PlanState>({
    plan: 'free',
    boostsRemaining: 0,
    loading: true,
  });

  useEffect(() => {
    if (!user) {
      setState({ plan: 'free', boostsRemaining: 0, loading: false });
      return;
    }
    const supabase = getSupabase();
    if (!supabase) {
      setState({ plan: 'free', boostsRemaining: 0, loading: false });
      return;
    }

    let cancelled = false;
    const load = async () => {
      const { data } = await supabase
        .from('hottr_entitlements')
        .select('plan, boosts_remaining')
        .eq('user_id', user.id)
        .maybeSingle();
      if (cancelled) return;
      const row = data as { plan?: string; boosts_remaining?: number } | null;
      const derived = derivePlanState(row);
      setState({ ...derived, loading: false });
    };
    load();

    const unsubscribe = subscribeTable(
      supabase,
      `hottr-entitlements-${user.id}`,
      {
        event: '*',
        table: 'hottr_entitlements',
        filter: `user_id=eq.${user.id}`,
      },
      load,
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [user?.id]);

  return state;
};
